import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import { Box, Skeleton, Typography } from "@mui/material";

import { useNavigate } from "react-router-dom";
import { headerSty, contentSty } from "../table-styles";
import { PATH_HOSPITAL } from "../../routes/path";
import { formatDate, formatTextType, limitToSeven } from "../../utils/url-id";

export default function DashboardTable({
  data,
  loading,
}: {
  data: any[];
  loading: boolean;
}) {
  const theme = useTheme();
  const headStyles = { ...headerSty, color: theme.palette.neutral.main };
  const contentStyles = { ...contentSty };

  const [openRow, setOpenRow] = React.useState<number | null>(null); // Track which row is open
  const navigate = useNavigate();

  const handleToggleOpen = (rowId: number) => {
    setOpenRow((prevOpenRow) => (prevOpenRow === rowId ? null : rowId));
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        my: 1,
        mx: 'auto',
        width: '97%',
        background: 'white',
        p: 2,
        border: 0,
        minHeight: 450,
        boxShadow: 'none'
      }}
    >
      <Table
        sx={{
          minWidth: 650,
          my: 2,
          paddingRight: 2,
          border: 0,
        }}
        aria-label="simple table"
      >
        <TableHead sx={{ paddingX: 2, border: 0 }}>
          <TableRow>
            <TableCell sx={{ ...headStyles, width: "15%" }}>
              Hospital ID
            </TableCell>
            <TableCell sx={{ ...headStyles, width: "15%" }}>
              Hospital Name
            </TableCell>
            <TableCell
              sx={{ ...headStyles, color: theme.palette.neutral.main }}
            >
              Category
            </TableCell>

            <TableCell sx={{ ...headStyles }}>Hospital size</TableCell>
            <TableCell sx={{ ...headStyles }}>Date Joined</TableCell>
            <TableCell sx={{ ...headStyles }}>Verification</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row: any, index: number) => (
            <TableRow
              key={row?._id}
              onClick={() => navigate(`${PATH_HOSPITAL.root}/${row?._id}`)}
            >
              <TableCell sx={contentSty}>{limitToSeven(row._id)}</TableCell>
              <TableCell sx={{ ...contentStyles }}>
                {formatTextType(row?.name ?? "")}
              </TableCell>
              <TableCell sx={{ ...contentStyles }}>
                {formatTextType(row?.facility_type)}
              </TableCell>
              <TableCell sx={{ ...contentStyles }}>
                {formatTextType(row?.facility_size)}
              </TableCell>
              <TableCell sx={{ ...contentStyles }}>
                {formatDate(row?.created_at ?? row?.createdAt)}
              </TableCell>
              <TableCell
                sx={{ ...contentStyles }}
              >{row?.verification ?? "-"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {data?.length === 0 && !loading && (
        <Box
          sx={{
            height: 200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, color: theme.palette.neutral.main }}
          >
            No Facility data available.
          </Typography>
        </Box>
      )}
      {loading && (
        <Box sx={{ width: "96%", mx: "auto" }}>
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
        </Box>
      )}
    </TableContainer>
  );
}

const EmpStatus: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        margin: "10px",
        paddingY: 1,
        paddingX: 2,
        borderRadius: "3px",
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.lightBlue.main,
      }}
    >
      {children}
    </Box>
  );
};
